import React from 'react';
import {
  Button,
  Card,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { runtimeQueryKeys } from '../../services/runtimeQueryKeys';
import { runtimeHistoryService } from '../../services/runtimeOperationalService';
import useRuntimeQuery from '../../hooks/useRuntimeQuery';
import {
  RuntimeCorrelationChip,
  RuntimeEmptyState,
  RuntimeErrorState,
  RuntimeLoadingState,
} from '../common/runtime';
import { buildApiErrorMessage } from '../../utils/apiError';

const { Title, Text } = Typography;

const STATUS_COLOR = {
  success: 'green',
  completed: 'green',
  running: 'processing',
  pending: 'gold',
  failed: 'red',
  error: 'red',
  cancelled: 'default',
};

const QUICK_FILTERS = [
  { key: 'scheduler', label: 'scheduler' },
  { key: 'scheduled_downgrade', label: 'scheduled_downgrade' },
  { key: 'scheduled_cancel', label: 'scheduled_cancel' },
  { key: 'lifecycle_materializer', label: 'lifecycle materializer' },
];

const matchesQuickFilter = (row, quickFilter) => {
  if (!quickFilter) return true;
  const operationType = String(row?.operation_type || '').trim().toLowerCase();
  if (!operationType) return false;

  if (quickFilter === 'scheduler') return operationType.includes('scheduler');
  if (quickFilter === 'scheduled_downgrade') return operationType.includes('scheduled_downgrade');
  if (quickFilter === 'scheduled_cancel') return operationType.includes('scheduled_cancel');
  if (quickFilter === 'lifecycle_materializer') {
    return operationType.includes('lifecycle') || operationType.includes('materializer');
  }
  return true;
};

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString('pt-BR');
};

const formatDuration = (value) => {
  if (value === null || value === undefined) {
    return '-';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }
  return `${numeric} ms`;
};

const getStatusTag = (value) => {
  const normalized = String(value || '').toLowerCase();
  const color = STATUS_COLOR[normalized] || 'default';
  return <Tag color={color}>{String(value || '-').toUpperCase()}</Tag>;
};

const PlatformRuntimeHistory = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = React.useState({
    status: undefined,
    tenant_id: '',
    operation_type: undefined,
  });
  const [appliedFilters, setAppliedFilters] = React.useState({});
  const [quickFilter, setQuickFilter] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);

  const queryParams = React.useMemo(
    () => ({
      ...appliedFilters,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }),
    [appliedFilters, page, pageSize]
  );

  const {
    data: historyResponse,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useRuntimeQuery({
    queryKey: runtimeQueryKeys.executionsList(queryParams),
    queryFn: async () => {
      const response = await runtimeHistoryService.listExecutions(queryParams);
      return response.data;
    },
  });

  const executionItems = Array.isArray(historyResponse?.items) ? historyResponse.items : [];
  const total = Number(historyResponse?.total || 0);
  const visibleItems = React.useMemo(
    () => executionItems.filter((item) => matchesQuickFilter(item, quickFilter)),
    [executionItems, quickFilter]
  );

  const operationTypeOptions = React.useMemo(() => {
    const all = executionItems
      .map((item) => String(item?.operation_type || '').trim())
      .filter(Boolean);

    const unique = [...new Set(all)].sort();
    return unique.map((value) => ({ label: value, value }));
  }, [executionItems]);

  const statusOptions = React.useMemo(() => {
    const all = executionItems
      .map((item) => String(item?.status || '').trim())
      .filter(Boolean);

    const unique = [...new Set(all)].sort();
    return unique.map((value) => ({ label: value.toUpperCase(), value }));
  }, [executionItems]);

  const handleApplyFilters = () => {
    const normalized = {
      status: filters.status || undefined,
      operation_type: filters.operation_type || undefined,
      tenant_id: String(filters.tenant_id || '').trim() || undefined,
    };

    setAppliedFilters(normalized);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ status: undefined, tenant_id: '', operation_type: undefined });
    setAppliedFilters({});
    setQuickFilter('');
    setPage(1);
  };

  const handleOpenDetail = (executionId) => {
    const normalized = String(executionId || '').trim();
    if (!normalized) {
      return;
    }
    navigate(`/platform/saas-billing/runtime/history/executions/${encodeURIComponent(normalized)}`);
  };

  const columns = [
    {
      title: 'Execution ID',
      dataIndex: 'execution_id',
      key: 'execution_id',
      render: (value) => value || '-',
    },
    {
      title: 'Correlation ID',
      dataIndex: 'correlation_id',
      key: 'correlation_id',
      render: (value) => <RuntimeCorrelationChip correlationId={value} />,
    },
    {
      title: 'Operation',
      dataIndex: 'operation_type',
      key: 'operation_type',
      render: (value) => value || '-',
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      render: (value) => value || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => getStatusTag(value),
    },
    {
      title: 'Dry Run',
      dataIndex: 'dry_run',
      key: 'dry_run',
      render: (value) => (value ? <Tag color="blue">SIM</Tag> : <Tag>NAO</Tag>),
    },
    {
      title: 'Started At',
      dataIndex: 'started_at',
      key: 'started_at',
      render: (value) => formatDateTime(value),
    },
    {
      title: 'Duration',
      dataIndex: 'duration_ms',
      key: 'duration_ms',
      render: (value) => formatDuration(value),
    },
    {
      title: 'Acoes',
      key: 'actions',
      render: (_, record) => (
        <Button size="small" type="link" onClick={() => handleOpenDetail(record.execution_id)}>
          Ver detalhe
        </Button>
      ),
    },
  ];

  const apiError = isError
    ? buildApiErrorMessage(error, 'Nao foi possivel carregar o Runtime History.')
    : null;

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginTop: 0 }}>
        Runtime History
      </Title>
      <Text type="secondary">Lista read-only de execucoes do runtime SaaS.</Text>

      <Card style={{ marginTop: 16 }}>
        <Space wrap size={12}>
          <Select
            placeholder="Status"
            value={filters.status}
            options={statusOptions}
            onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
            allowClear
            style={{ minWidth: 180 }}
          />

          <Input
            placeholder="Tenant ID"
            value={filters.tenant_id}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, tenant_id: event.target.value }))
            }
            style={{ minWidth: 220 }}
          />

          <Select
            placeholder="Operation Type"
            value={filters.operation_type}
            options={operationTypeOptions}
            onChange={(value) => setFilters((prev) => ({ ...prev, operation_type: value }))}
            allowClear
            style={{ minWidth: 220 }}
          />

          <Button type="primary" onClick={handleApplyFilters}>
            Aplicar filtros
          </Button>

          <Button onClick={handleClearFilters}>Limpar</Button>

          <Button onClick={() => refetch()} loading={isFetching}>
            Atualizar
          </Button>

          <Text type="secondary">Filtros rapidos:</Text>
          {QUICK_FILTERS.map((preset) => (
            <Button
              key={preset.key}
              size="small"
              type={quickFilter === preset.key ? 'primary' : 'default'}
              onClick={() => {
                setQuickFilter((current) => (current === preset.key ? '' : preset.key));
                setPage(1);
              }}
            >
              {preset.label}
            </Button>
          ))}
        </Space>
      </Card>

      <Card style={{ marginTop: 16 }}>
        {isLoading ? <RuntimeLoadingState /> : null}

        {!isLoading && isError ? (
          <RuntimeErrorState
            title="Erro ao carregar Runtime History"
            description={apiError?.message || 'Falha inesperada ao consultar historico.'}
            onRetry={() => refetch()}
          />
        ) : null}

        {!isLoading && !isError && visibleItems.length === 0 ? (
          <RuntimeEmptyState
            title="Sem execucoes para os filtros atuais"
            description="Tente limpar os filtros rapidos ou ajustar os filtros de busca."
          />
        ) : null}

        {!isLoading && !isError && visibleItems.length > 0 ? (
          <Table
            rowKey="execution_id"
            columns={columns}
            dataSource={visibleItems}
            pagination={{
              current: page,
              pageSize,
              total: quickFilter ? visibleItems.length : total,
              showSizeChanger: true,
              onChange: (nextPage, nextPageSize) => {
                setPage(nextPage);
                if (nextPageSize !== pageSize) {
                  setPageSize(nextPageSize);
                }
              },
            }}
          />
        ) : null}
      </Card>
    </div>
  );
};

export default PlatformRuntimeHistory;